/**
 * Lightweight Async Select for Advanced Search
 * Simplified version without modal form styling and requirements
 */

import React, { useEffect, useMemo, useState } from 'react';
import Select, { components } from 'react-select';
import type { CSSObjectWithLabel, MenuListProps } from 'react-select';
import { SelectOption } from '../../shared/utils/asyncSelectApi';
import { useAsyncSelect } from '../../shared/hooks/useAsyncSelect';

interface AsyncSearchSelectProps {
  value: string;
  onChange: (value: string | null) => void;
  apiEndpoint: string;
  searchField: string;
  valueField: string;
  labelField: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Async select for advanced search with API-based pagination
 * Uses regular Select with manual async loading for full control
 */
const AsyncSearchSelect: React.FC<AsyncSearchSelectProps> = ({
  value,
  onChange,
  apiEndpoint,
  searchField,
  valueField,
  labelField,
  placeholder = 'Select value',
  disabled = false,
}) => {
  // Use shared hook for all async select logic with state management
  const {
    options,
    loadOptions,
    handleMenuScrollToBottom,
    scrollContainerRef,
    fetchSelectedValue,
    isLoadingMore,
  } = useAsyncSelect({
    apiEndpoint,
    searchField,
    valueField,
    labelField,
  });

  // State for selected option and input value
  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null
  );
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Load options when input changes (with debounce handled by hook)
   */
  useEffect(() => {
    setIsLoading(true);
    loadOptions(inputValue, () => {
      setIsLoading(false);
    });
  }, [inputValue, loadOptions]);

  /**
   * Load selected value when value prop changes
   * Note: For DataGrid filters with ID values where valueField !== searchField,
   * we display the ID directly since backend may not support ID field searches
   */
  useEffect(() => {
    const loadValue = async () => {
      if (!value) {
        setSelectedOption(null);
        return;
      }

      // Skip loading if value hasn't changed
      if (selectedOption && selectedOption.value === value) {
        return;
      }

      // If valueField is different from searchField and ends with _id,
      // we likely can't search by it (e.g., grade_id vs grade_name).
      // Just show the value as label without API call.
      if (valueField !== searchField && valueField.endsWith('_id')) {
        setSelectedOption({ label: value, value: String(value) });
        return;
      }

      // Fetch the selected value from API
      try {
        const option = await fetchSelectedValue(value);
        setSelectedOption(option || { label: value, value: String(value) });
      } catch (error) {
        console.error('Error loading selected value:', error);
        setSelectedOption({ label: value, value: String(value) });
      }
    };

    loadValue();
  }, [value, valueField, searchField, selectedOption, fetchSelectedValue]);

  // Custom MenuList component to detect scroll to bottom and show loading footer
  const MenuList = (props: MenuListProps<SelectOption>) => {
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;

      const bottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

      if (bottom && !isLoadingMore) {
        handleMenuScrollToBottom();
      }
    };

    // Attach scroll container ref from hook
    const combinedRef = (element: HTMLDivElement | null) => {
      scrollContainerRef(element);
      if (props.innerRef) {
        if (typeof props.innerRef === 'function') {
          props.innerRef(element);
        } else {
          (
            props.innerRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = element;
        }
      }
    };

    return (
      <>
        <components.MenuList
          {...props}
          innerRef={combinedRef}
          innerProps={{
            ...props.innerProps,
            onScroll: handleScroll,
          }}>
          {props.children}
        </components.MenuList>
        {isLoadingMore && (
          <div
            style={{
              padding: '8px 12px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '14px',
              borderTop: '1px solid #e5e7eb',
            }}>
            ⏳ Loading more...
          </div>
        )}
      </>
    );
  };

  // Memoize styles to prevent unnecessary re-renders
  const selectStyles = useMemo(
    () => ({
      control: (base: CSSObjectWithLabel) => ({
        ...base,
        minHeight: '38px',
        borderColor: '#d1d5db',
      }),
      menuPortal: (base: CSSObjectWithLabel) => ({
        ...base,
        zIndex: 9999,
      }),
      loadingMessage: (base: CSSObjectWithLabel) => ({
        ...base,
        padding: '8px 12px',
        color: '#6b7280',
      }),
    }),
    []
  );

  return (
    <Select
      value={selectedOption}
      onChange={option => {
        setSelectedOption(option as SelectOption | null);
        onChange(option ? (option as SelectOption).value : null);
      }}
      onInputChange={(newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          setInputValue(newValue);
        }
      }}
      options={options}
      components={{ MenuList }}
      isClearable
      isLoading={isLoading}
      isDisabled={disabled}
      placeholder={placeholder}
      menuPortalTarget={document.body}
      styles={selectStyles}
      filterOption={() => true}
      aria-label={placeholder}
      aria-invalid={false}
      aria-describedby={undefined}
      inputId={`async-select-${valueField}`}
      classNamePrefix="async-select"
    />
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(AsyncSearchSelect);
